import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import { EventMediaItem } from '../utils/eventMedia';
import { getEventsDateFilter, isEventPastToday, toUtcIsoDate } from '../utils/eventDateWindow';

export interface Event {
  id: string;
  title: string;
  description?: string | null;
  image_url?: string | null;
  media_items?: EventMediaItem[] | null;
  start_date: string;
  start_time?: string;
  end_date?: string;
  end_time?: string;
  location?: string;
  address?: string | null;
  organizer_name?: string | null;
  ministry?: string | null;
  status?: string;
  category?: string;
  is_registration_required?: boolean | null;
  hide_registration_details_when_no_public_tickets?: boolean | null;
  appointments_enabled?: boolean | null;
  appointment_booking_mode?: 'single' | 'multiple' | null;
  paypal_account?: 'ncof' | 'event_paypal' | 'none' | null;
  event_registrations?: { count: number }[];
}

export interface CreateEventInput {
  title: string;
  description?: string | null;
  image_url?: string | null;
  media_items?: EventMediaItem[] | null;
  start_date: string;
  end_date?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  location?: string | null;
  address?: string | null;
  organizer_name?: string | null;
  ministry?: string | null;
  category?: string | null;
  status?: string;
  is_registration_required?: boolean;
  hide_registration_details_when_no_public_tickets?: boolean;
  appointments_enabled?: boolean;
  appointment_booking_mode?: 'single' | 'multiple';
  paypal_account?: 'ncof' | 'event_paypal' | 'none';
}

export interface UpdateEventInput {
  eventId: string;
  title?: string;
  description?: string | null;
  image_url?: string | null;
  media_items?: EventMediaItem[] | null;
  start_date?: string;
  end_date?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  location?: string | null;
  address?: string | null;
  organizer_name?: string | null;
  ministry?: string | null;
  category?: string | null;
  status?: string;
  is_registration_required?: boolean;
  hide_registration_details_when_no_public_tickets?: boolean;
  appointments_enabled?: boolean;
  appointment_booking_mode?: 'single' | 'multiple';
  paypal_account?: 'ncof' | 'event_paypal' | 'none';
}

export interface EventAppointmentSlot {
  id?: string;
  event_id?: string;
  label: string;
  starts_at: string;
  ends_at: string;
  capacity: number;
  status: 'active' | 'inactive';
  sort_order: number;
  booked_count?: number;
  remaining_capacity?: number;
}

export interface EventTicketType {
  id?: string;
  event_id?: string;
  name: string;
  description?: string | null;
  price_cents: number;
  deposit_amount_cents?: number;
  currency: string;
  is_special?: boolean;
  is_sponsor_ticket?: boolean;
  is_locked?: boolean;
  access_code?: string | null;
  is_exclusive?: boolean;
  exclusive_enforce_once?: boolean;
  allowed_emails?: string[];
  requires_registration_form?: boolean;
  is_group_ticket?: boolean;
  min_group_size?: number;
  capacity?: number | null;
  per_order_limit: number;
  sales_start?: string | null;
  sales_end?: string | null;
  status: 'active' | 'inactive' | 'sold_out';
  sort_order: number;
}

export interface EventOrderFormField {
  id?: string;
  form_id?: string;
  field_key: string;
  label: string;
  field_type:
    | 'short_text'
    | 'long_text'
    | 'email'
    | 'phone'
    | 'number'
    | 'select'
    | 'multi_select'
    | 'checkbox'
    | 'date';
  is_required: boolean;
  sort_order: number;
  options?: unknown;
  validation?: unknown;
  visibility_rules?: unknown;
  placeholder?: string | null;
  help_text?: string | null;
  field_group?: 'registration' | 'waiver';
}

export interface EventOrderForm {
  id?: string;
  event_id?: string;
  is_enabled: boolean;
  waiver_enabled?: boolean;
  fields: EventOrderFormField[];
}

export type UseEventsOptions = {
  upcomingOnly?: boolean;
  includePastMonths?: number;
};

function resolveUseEventsOptions(arg: boolean | UseEventsOptions = true): Required<UseEventsOptions> {
  if (typeof arg === 'boolean') {
    return { upcomingOnly: arg, includePastMonths: 0 };
  }
  return {
    upcomingOnly: arg.upcomingOnly ?? true,
    includePastMonths: arg.includePastMonths ?? 0,
  };
}

function deriveEventAddress(location?: string | null, ministry?: string | null) {
  return location?.trim() || ministry?.trim() || 'TBD';
}

function deriveOrganizerName(organizerName?: string | null, ministry?: string | null) {
  return organizerName?.trim() || ministry?.trim() || 'NCOF';
}

async function updateEventAsAdmin(eventId: string, updates: Record<string, unknown>) {
  const { data, error } = await supabase.functions.invoke('admin-update-event', {
    body: { eventId, updates },
  });

  if (error) throw error;
  if (data && (data as { error?: string }).error) {
    throw new Error((data as { error: string }).error);
  }

  const event = (data as { event?: Event } | null)?.event;
  if (!event) {
    throw new Error('Event was not updated. You may not have permission to edit this event.');
  }

  return event;
}

async function softDeleteEventAsAdmin(eventId: string) {
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  const { data, error } = await supabase.functions.invoke('admin-update-event', {
    body: {
      eventId,
      updates: {
        status: 'completed',
        start_date: yesterday,
        end_date: yesterday,
      },
    },
  });

  if (error) throw error;
  if (data && (data as { error?: string }).error) {
    throw new Error((data as { error: string }).error);
  }

  const event = (data as { event?: Event } | null)?.event;
  if (!event) {
    throw new Error('Event was not deleted. You may not have permission to delete this event.');
  }

  return event;
}

export const useEvents = (upcomingOnlyOrOptions: boolean | UseEventsOptions = true) => {
  const options = resolveUseEventsOptions(upcomingOnlyOrOptions);
  return useQuery<Event[]>({
    queryKey: ['events', options.upcomingOnly, options.includePastMonths],
    queryFn: async () => {
      const today = toUtcIsoDate();
      const { data: activeEvents, error: activeEventsError } = await supabase
        .from('events')
        .select('id, start_date, end_date, status')
        .neq('status', 'completed')
        .lte('start_date', today);

      if (activeEventsError) throw activeEventsError;

      const pastEventIds = (activeEvents ?? [])
        .filter((event) => isEventPastToday(event as Pick<Event, 'start_date' | 'end_date'>, today))
        .map((event) => event.id as string);

      if (pastEventIds.length > 0) {
        const { error: completeError } = await supabase
          .from('events')
          .update({ status: 'completed' })
          .in('id', pastEventIds);

        // Event admins can list assigned events but cannot update event records.
        if (completeError) {
          console.warn('Could not auto-complete past events', completeError.message);
        }
      }

      let query = supabase
        .from('events')
        .select('*, event_registrations(count)')
        .order('start_date', { ascending: true });

      const dateFilter = getEventsDateFilter({
        upcomingOnly: options.upcomingOnly,
        includePastMonths: options.includePastMonths,
        todayIsoDate: today,
      });
      if (dateFilter.kind === 'gteStart') {
        query = query.gte('start_date', dateFilter.value);
      } else if (dateFilter.kind === 'onOrAfter') {
        query = query.or(`start_date.gte.${dateFilter.value},end_date.gte.${dateFilter.value}`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Event[];
    },
  });
};

export const useEventTicketTypes = (eventId?: string | null) => {
  return useQuery<EventTicketType[]>({
    queryKey: ['eventTicketTypes', eventId],
    enabled: Boolean(eventId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('event_ticket_types')
        .select('*')
        .eq('event_id', eventId as string)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });

      if (error) throw error;
      const ticketTypes = (data as EventTicketType[]) ?? [];
      if (ticketTypes.length === 0) return [];

      const ticketTypeIds = ticketTypes
        .map((ticketType) => String(ticketType.id || '').trim())
        .filter(Boolean);
      if (ticketTypeIds.length === 0) return ticketTypes;

      const { data: allowlistRows, error: allowlistError } = await supabase
        .from('event_ticket_type_allowed_emails')
        .select('ticket_type_id, email_normalized')
        .in('ticket_type_id', ticketTypeIds);
      if (allowlistError) {
        throw new Error(`Could not load exclusive ticket allowlist: ${allowlistError.message}`);
      }

      const allowlistByTicketType = new Map<string, string[]>();
      for (const row of (allowlistRows || []) as Array<{ ticket_type_id: string; email_normalized: string }>) {
        const ticketTypeId = String(row.ticket_type_id || '').trim();
        const email = String(row.email_normalized || '').trim().toLowerCase();
        if (!ticketTypeId || !email) continue;
        const existing = allowlistByTicketType.get(ticketTypeId) ?? [];
        if (!existing.includes(email)) existing.push(email);
        allowlistByTicketType.set(ticketTypeId, existing);
      }

      return ticketTypes.map((ticketType) => ({
        ...ticketType,
        deposit_amount_cents: Number(ticketType.deposit_amount_cents ?? 0),
        is_exclusive: Boolean(ticketType.is_exclusive),
        exclusive_enforce_once: Boolean(ticketType.exclusive_enforce_once ?? true),
        allowed_emails: allowlistByTicketType.get(String(ticketType.id || '')) ?? [],
        requires_registration_form: ticketType.requires_registration_form !== false,
      }));
    },
  });
};

export const useEventAppointmentSlots = (eventId?: string | null) => {
  return useQuery<EventAppointmentSlot[]>({
    queryKey: ['eventAppointmentSlots', eventId],
    enabled: Boolean(eventId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('event_appointment_slots')
        .select('*')
        .eq('event_id', eventId as string)
        .order('starts_at', { ascending: true })
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });

      if (error) throw error;
      return ((data as EventAppointmentSlot[] | null) ?? []).map((slot, index) => ({
        ...slot,
        capacity: Math.max(1, Number(slot.capacity || 1)),
        status: slot.status === 'inactive' ? 'inactive' : 'active',
        sort_order: Number.isFinite(slot.sort_order) ? slot.sort_order : index,
      }));
    },
  });
};

export const useSaveEventAppointmentSlots = (eventId?: string | null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (slots: EventAppointmentSlot[]) => {
      if (!eventId) throw new Error('Event is required to save appointment slots');

      const { error: deleteError } = await supabase
        .from('event_appointment_slots')
        .delete()
        .eq('event_id', eventId);

      if (deleteError) throw deleteError;

      if (slots.length === 0) return [];

      const rows = slots.map((slot, index) => ({
        event_id: eventId,
        label: slot.label.trim(),
        starts_at: slot.starts_at,
        ends_at: slot.ends_at,
        capacity: Math.max(1, Number(slot.capacity || 1)),
        status: slot.status === 'inactive' ? 'inactive' : 'active',
        sort_order: Number.isFinite(slot.sort_order) ? slot.sort_order : index,
      }));

      const { data, error } = await supabase
        .from('event_appointment_slots')
        .insert(rows)
        .select('*');

      if (error) throw error;
      return (data as EventAppointmentSlot[] | null) ?? [];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eventAppointmentSlots', eventId] });
    },
  });
};

const EVENT_TICKET_ALLOWLIST_EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

function buildEventTicketTypeSaveRow(eventId: string, ticketType: EventTicketType, index: number) {
  return {
    event_id: eventId,
    name: ticketType.name,
    description: ticketType.description ?? null,
    price_cents: Number(ticketType.price_cents ?? 0),
    deposit_amount_cents: Math.max(0, Number(ticketType.deposit_amount_cents ?? 0)),
    currency: ticketType.currency || 'USD',
    is_special: Boolean(ticketType.is_special),
    is_sponsor_ticket: Boolean(ticketType.is_sponsor_ticket),
    is_locked: Boolean(ticketType.is_locked),
    access_code: ticketType.is_locked ? ticketType.access_code?.trim() || null : null,
    is_exclusive: Boolean(ticketType.is_exclusive),
    exclusive_enforce_once: Boolean(ticketType.exclusive_enforce_once ?? true),
    requires_registration_form: ticketType.requires_registration_form !== false,
    is_group_ticket: Boolean(ticketType.is_group_ticket),
    min_group_size: Math.max(1, Number(ticketType.min_group_size ?? 6)),
    capacity: ticketType.capacity ?? null,
    per_order_limit: Number(ticketType.per_order_limit ?? 10),
    sales_start: ticketType.sales_start ?? null,
    sales_end: ticketType.sales_end ?? null,
    status: ticketType.status || 'active',
    sort_order: Number.isFinite(ticketType.sort_order) ? ticketType.sort_order : index,
  };
}

function normalizeTicketAllowlistEmails(emails: string[] | undefined): string[] {
  return Array.from(
    new Set(
      (emails ?? [])
        .map((value) => String(value || '').trim().toLowerCase())
        .filter((value) => EVENT_TICKET_ALLOWLIST_EMAIL_PATTERN.test(value))
    )
  );
}

export const useSaveEventTicketTypes = (eventId?: string | null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ticketTypes: EventTicketType[]) => {
      if (!eventId) throw new Error('Event is required to save ticket types');

      const { data: existingRows, error: existingError } = await supabase
        .from('event_ticket_types')
        .select('id, is_special, requires_registration_form')
        .eq('event_id', eventId);

      if (existingError) {
        const message = String(existingError.message || '');
        if (/column .*is_special/i.test(message)) {
          throw new Error(
            'Database is missing event_ticket_types.is_special. Run the migration 20260418000001_event_ticket_types_add_is_special.sql before saving ticket types.'
          );
        }
        if (/column .*requires_registration_form/i.test(message)) {
          throw new Error(
            'Database is missing event_ticket_types.requires_registration_form. Run the migration 20260819000000_add_requires_registration_form_to_event_ticket_types.sql before saving ticket types.'
          );
        }
        throw existingError;
      }

      const existingIds = new Set(
        ((existingRows as Array<{ id?: string | null }> | null) ?? [])
          .map((row) => String(row.id || '').trim())
          .filter(Boolean)
      );

      const drafts = ticketTypes.map((ticketType, index) => ({
        ticketType,
        index,
        id: String(ticketType.id || '').trim(),
      }));
      const toUpdate = drafts.filter((draft) => draft.id && existingIds.has(draft.id));
      const toInsert = drafts.filter((draft) => !draft.id || !existingIds.has(draft.id));
      const idsToKeep = new Set(toUpdate.map((draft) => draft.id));
      const idsToDelete = Array.from(existingIds).filter((id) => !idsToKeep.has(id));

      // Preserve existing ticket type IDs so special registration links stay stable.
      if (idsToDelete.length > 0) {
        const { error: deleteError } = await supabase
          .from('event_ticket_types')
          .delete()
          .eq('event_id', eventId)
          .in('id', idsToDelete);
        if (deleteError) throw deleteError;
      }

      if (ticketTypes.length === 0) return [];

      const savedByIndex: EventTicketType[] = new Array(ticketTypes.length);

      if (toUpdate.length > 0) {
        for (const draft of toUpdate) {
          const { error: pendingNameError } = await supabase
            .from('event_ticket_types')
            .update({ name: `__pending_${draft.id}` })
            .eq('id', draft.id)
            .eq('event_id', eventId);
          if (pendingNameError) throw pendingNameError;
        }

        for (const draft of toUpdate) {
          const { data, error } = await supabase
            .from('event_ticket_types')
            .update(buildEventTicketTypeSaveRow(eventId, draft.ticketType, draft.index))
            .eq('id', draft.id)
            .eq('event_id', eventId)
            .select('*')
            .single();
          if (error) throw error;
          savedByIndex[draft.index] = data as EventTicketType;
        }
      }

      for (const draft of toInsert) {
        const { data, error } = await supabase
          .from('event_ticket_types')
          .insert(buildEventTicketTypeSaveRow(eventId, draft.ticketType, draft.index))
          .select('*')
          .single();
        if (error) throw error;
        savedByIndex[draft.index] = data as EventTicketType;
      }

      const persistedRows = savedByIndex.filter(Boolean);
      const persistedIds = persistedRows
        .map((row) => String(row.id || '').trim())
        .filter(Boolean);

      if (persistedIds.length > 0) {
        const { error: allowlistDeleteError } = await supabase
          .from('event_ticket_type_allowed_emails')
          .delete()
          .in('ticket_type_id', persistedIds);
        if (allowlistDeleteError) {
          throw new Error(`Could not save exclusive ticket allowlist: ${allowlistDeleteError.message}`);
        }
      }

      const allowlistInserts: Array<{ ticket_type_id: string; email_normalized: string }> = [];
      ticketTypes.forEach((ticketType, index) => {
        const ticketTypeId = String(savedByIndex[index]?.id || '').trim();
        if (!ticketTypeId || !ticketType.is_exclusive) return;
        normalizeTicketAllowlistEmails(ticketType.allowed_emails).forEach((email) => {
          allowlistInserts.push({
            ticket_type_id: ticketTypeId,
            email_normalized: email,
          });
        });
      });

      if (allowlistInserts.length > 0) {
        const { error: allowlistInsertError } = await supabase
          .from('event_ticket_type_allowed_emails')
          .insert(allowlistInserts);
        if (allowlistInsertError) {
          throw new Error(`Could not save exclusive ticket allowlist: ${allowlistInsertError.message}`);
        }
      }

      return persistedRows;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eventTicketTypes', eventId] });
    },
  });
};

export const useEventOrderForm = (eventId?: string | null) => {
  return useQuery<EventOrderForm | null>({
    queryKey: ['eventOrderForm', eventId],
    enabled: Boolean(eventId),
    queryFn: async () => {
      const { data: form, error: formError } = await supabase
        .from('event_order_forms')
        .select('*')
        .eq('event_id', eventId as string)
        .maybeSingle();

      if (formError) throw formError;
      if (!form) return null;

      const { data: fields, error: fieldsError } = await supabase
        .from('event_order_form_fields')
        .select('*')
        .eq('form_id', form.id)
        .order('sort_order', { ascending: true });

      if (fieldsError) throw fieldsError;

      return {
        ...(form as Omit<EventOrderForm, 'fields'>),
        fields: (fields as EventOrderFormField[]) ?? [],
      };
    },
  });
};

export const useSaveEventOrderForm = (eventId?: string | null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: EventOrderForm) => {
      if (!eventId) throw new Error('Event is required to save order form');

      const { data: form, error: formError } = await supabase
        .from('event_order_forms')
        .upsert(
          {
            event_id: eventId,
            is_enabled: payload.is_enabled,
            waiver_enabled: payload.waiver_enabled === true,
          },
          { onConflict: 'event_id' },
        )
        .select('*')
        .single();

      if (formError) {
        if (/waiver_enabled/i.test(formError.message)) {
          throw new Error(
            'Database is missing event_order_forms.waiver_enabled. Apply migration 20261024140000_add_event_waiver_questions.sql before saving the waiver.',
          );
        }
        throw formError;
      }

      const { error: deleteError } = await supabase
        .from('event_order_form_fields')
        .delete()
        .eq('form_id', form.id);

      if (deleteError) throw deleteError;

      if (!payload.fields || payload.fields.length === 0) {
        return { ...form, fields: [] };
      }

      const rows = payload.fields.map((field, index) => ({
        form_id: form.id,
        field_key: field.field_key,
        label: field.label,
        field_type: field.field_type,
        is_required: field.is_required,
        sort_order: Number.isFinite(field.sort_order) ? field.sort_order : index,
        options: field.options ?? [],
        validation: field.validation ?? {},
        visibility_rules: field.visibility_rules ?? {},
        placeholder: field.placeholder ?? null,
        help_text: field.help_text ?? null,
        field_group: field.field_group === 'waiver' ? 'waiver' : 'registration',
      }));

      const { data: fields, error: fieldsError } = await supabase
        .from('event_order_form_fields')
        .insert(rows)
        .select('*');

      if (fieldsError) {
        if (/field_group/i.test(fieldsError.message)) {
          throw new Error(
            'Database is missing event_order_form_fields.field_group. Apply migration 20261024140000_add_event_waiver_questions.sql before saving waiver questions.',
          );
        }
        throw fieldsError;
      }

      return {
        ...form,
        fields,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eventOrderForm', eventId] });
    },
  });
};

export const useCreateEvent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateEventInput) => {
      const normalizedStartTime = payload.start_time && payload.start_time.length === 5
        ? `${payload.start_time}:00`
        : payload.start_time ?? null;
      const normalizedEndTime = payload.end_time && payload.end_time.length === 5
        ? `${payload.end_time}:00`
        : payload.end_time ?? normalizedStartTime;

      const { data, error } = await supabase
        .from('events')
        .insert({
          title: payload.title,
          description: payload.description ?? '',
          image_url: payload.image_url ?? null,
          media_items: payload.media_items ?? [],
          start_date: payload.start_date,
          end_date: payload.end_date ?? payload.start_date,
          start_time: normalizedStartTime,
          end_time: normalizedEndTime,
          location: payload.location ?? null,
          address: payload.address?.trim() || deriveEventAddress(payload.location, payload.ministry),
          organizer_name: deriveOrganizerName(payload.organizer_name, payload.ministry),
          ministry: payload.ministry ?? null,
          category: payload.category ?? 'general',
          status: payload.status ?? 'published',
          appointments_enabled: payload.appointments_enabled ?? false,
          appointment_booking_mode: payload.appointment_booking_mode ?? 'single',
          paypal_account: payload.paypal_account ?? 'ncof',
        })
        .select('*')
        .single();

      if (error) throw error;
      return data as Event;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
    },
  });
};

export const useUpdateEventStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ eventId, status }: { eventId: string; status: string }) => {
      const { data, error } = await supabase
        .from('events')
        .update({ status })
        .eq('id', eventId)
        .select('id, status');

      if (error) throw error;
      if (data && data.length > 0) {
        return { id: eventId, status } as Event;
      }

      const updated = await updateEventAsAdmin(eventId, { status });
      return updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
    },
  });
};

export const useUpdateEvent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateEventInput) => {
      const { eventId, ...rest } = payload;
      const updateBody: Record<string, unknown> = { ...rest };

      // events.description is NOT NULL in this project; persist blank instead of null.
      if (rest.description !== undefined) {
        updateBody.description = rest.description ?? '';
      }

      if (typeof rest.start_time === 'string') {
        updateBody.start_time = rest.start_time.length === 5 ? `${rest.start_time}:00` : rest.start_time;
      }
      if (typeof rest.end_time === 'string') {
        updateBody.end_time = rest.end_time.length === 5 ? `${rest.end_time}:00` : rest.end_time;
      }
      if (rest.location !== undefined || rest.ministry !== undefined || rest.address !== undefined) {
        updateBody.address =
          rest.address?.trim() || deriveEventAddress(rest.location ?? undefined, rest.ministry ?? undefined);
      }
      if (rest.organizer_name !== undefined || rest.ministry !== undefined) {
        updateBody.organizer_name = deriveOrganizerName(rest.organizer_name, rest.ministry);
      }

      const { data, error } = await supabase
        .from('events')
        .update(updateBody)
        .eq('id', eventId)
        .select('id');

      if (error) throw error;
      if (data && data.length > 0) {
        return { id: eventId, ...updateBody } as Event;
      }

      const updated = await updateEventAsAdmin(eventId, updateBody);
      return updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
};

export const IMHIM_WEBSITE_SITE_KEY = 'imhim';

export interface PublicWebsiteEventLink {
  site_key: string;
  event_id: string | null;
  event_title: string | null;
}

export const usePublicWebsiteEventLink = (siteKey: string) => {
  return useQuery<PublicWebsiteEventLink | null>({
    queryKey: ['publicWebsiteEventLink', siteKey],
    enabled: Boolean(siteKey),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('public_website_event_links')
        .select('site_key, event_id')
        .eq('site_key', siteKey)
        .maybeSingle();
      if (error) {
        const message = String(error.message || '');
        if (/public_website_event_links|does not exist|schema cache/i.test(message)) {
          return {
            site_key: siteKey,
            event_id: null,
            event_title: null,
          };
        }
        throw error;
      }
      if (!data) {
        return {
          site_key: siteKey,
          event_id: null,
          event_title: null,
        };
      }

      const eventId = String((data as { event_id?: string | null }).event_id || '').trim() || null;
      let eventTitle: string | null = null;
      if (eventId) {
        const { data: eventRow, error: eventError } = await supabase
          .from('events')
          .select('title')
          .eq('id', eventId)
          .maybeSingle();
        if (eventError) throw eventError;
        eventTitle = String((eventRow as { title?: string | null } | null)?.title || '').trim() || null;
      }

      return {
        site_key: String((data as { site_key?: string }).site_key || siteKey),
        event_id: eventId,
        event_title: eventTitle,
      };
    },
  });
};

export const useSavePublicWebsiteEventLink = (siteKey: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: { eventId: string; enabled: boolean }) => {
      const eventId = String(args.eventId || '').trim();
      if (!eventId) throw new Error('Event is required to update the website link.');

      const {
        data: { user },
      } = await supabase.auth.getUser();
      const updatedBy = user?.id ?? null;
      const updatedAt = new Date().toISOString();

      if (args.enabled) {
        const { error } = await supabase.from('public_website_event_links').upsert(
          {
            site_key: siteKey,
            event_id: eventId,
            updated_at: updatedAt,
            updated_by: updatedBy,
          },
          { onConflict: 'site_key' },
        );
        if (error) {
          const message = String(error.message || '');
          if (/public_website_event_links|does not exist|schema cache/i.test(message)) {
            throw new Error(
              'IMHIM website linking needs migration 20260911130000_add_public_website_event_links.sql applied first.',
            );
          }
          throw error;
        }
        return;
      }

      const { data: existing, error: existingError } = await supabase
        .from('public_website_event_links')
        .select('event_id')
        .eq('site_key', siteKey)
        .maybeSingle();
      if (existingError) {
        const message = String(existingError.message || '');
        if (/public_website_event_links|does not exist|schema cache/i.test(message)) {
          return;
        }
        throw existingError;
      }
      if (String((existing as { event_id?: string | null } | null)?.event_id || '') !== eventId) {
        return;
      }

      const { error } = await supabase
        .from('public_website_event_links')
        .update({
          event_id: null,
          updated_at: updatedAt,
          updated_by: updatedBy,
        })
        .eq('site_key', siteKey);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['publicWebsiteEventLink', siteKey] });
    },
  });
};

export const useDeleteEvent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (eventId: string) => {
      const { data, error } = await supabase
        .from('events')
        .delete()
        .eq('id', eventId)
        .select('id');

      if (error) throw error;
      if (data && data.length > 0) {
        return { id: eventId };
      }

      await softDeleteEventAsAdmin(eventId);
      return { id: eventId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
    },
  });
};
